import { Badge, Box, Flex, HStack, IconButton, Text } from "components/ui";
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
} from "components/ui/menu";
import { useEffect, useState } from "react";
import { MdAdd, MdDelete, MdMoreVert, MdOpenInNew } from "react-icons/md";
import { WidgetConfig, widgetService } from "services/widgetService";
import CreateWidgetModal from "./CreateWidgetModal";

const CHAT_WIDGET_URL = process.env.REACT_APP_CHAT_WIDGET_URL;

interface WidgetSelectorProps {
  selectedWidgetId: string | undefined;
  onSelect: (id: string) => void;
}

const WidgetSelector = ({
  selectedWidgetId,
  onSelect,
}: WidgetSelectorProps) => {
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    try {
      setLoading(true);
      const data = await widgetService.getAllWidgets();
      setWidgets(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Delete this widget?")) return;
    try {
      await widgetService.deleteWidget(id);
      if (selectedWidgetId === id) onSelect("");
      await loadWidgets();
    } catch {
      // ignore
    }
  };

  const handleWidgetCreated = async (widgetId: string) => {
    setIsModalOpen(false);
    await loadWidgets();
    onSelect(widgetId);
  };

  const getChatUrl = (widget: WidgetConfig) =>
    `${CHAT_WIDGET_URL}/?applicationToken=${widget.applicationToken}&userId=preview_user_${Date.now()}`;

  if (loading) {
    return (
      <Box py={3}>
        <Text fontSize="sm" color="var(--text-secondary-color)">
          Loading widgets…
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box
        w="full"
        overflowX="auto"
        pb={2}
        css={{
          "&::-webkit-scrollbar": { height: "4px" },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: "2px",
            background: "var(--border-default)",
          },
        }}
      >
        <HStack gap={2.5} align="stretch" w="max-content" minW="full">
          {widgets.map((widget) => {
            const isSelected = widget.id === selectedWidgetId;
            return (
              <Box
                key={widget.id}
                minW="200px"
                maxW="260px"
                p={3.5}
                borderRadius="10px"
                borderWidth="1px"
                borderColor={
                  isSelected
                    ? "var(--brand-primary, #3B82F6)"
                    : "var(--border-default)"
                }
                bg={
                  isSelected ? "rgba(59, 130, 246, 0.06)" : "var(--bg-primary)"
                }
                cursor="pointer"
                onClick={() => onSelect(widget.id!)}
                transition="background-color 0.15s, border-color 0.15s"
                _hover={isSelected ? undefined : { bg: "var(--bg-secondary)" }}
              >
                <Flex direction="column" gap={2.5}>
                  {/* Header row: name + actions */}
                  <Flex justify="space-between" align="start" gap={1}>
                    <Text
                      fontSize="sm"
                      fontWeight={500}
                      color="var(--text-primary)"
                      lineClamp={2}
                      flex={1}
                      minW={0}
                      m={0}
                    >
                      {widget.name}
                    </Text>
                    <HStack gap={0} flexShrink={0}>
                      {CHAT_WIDGET_URL && widget.applicationToken && (
                        <IconButton
                          variant="ghost"
                          size="2xs"
                          aria-label="Preview widget"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              getChatUrl(widget),
                              "_blank",
                              "noopener,noreferrer",
                            );
                          }}
                        >
                          <MdOpenInNew />
                        </IconButton>
                      )}
                      <MenuRoot>
                        <MenuTrigger asChild>
                          <IconButton
                            variant="ghost"
                            size="2xs"
                            aria-label="Widget actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MdMoreVert />
                          </IconButton>
                        </MenuTrigger>
                        <MenuContent>
                          <MenuItem
                            value="delete"
                            onClick={(e: React.MouseEvent) =>
                              handleDelete(widget.id!, e)
                            }
                            color="red.500"
                          >
                            <MdDelete />
                            Delete
                          </MenuItem>
                        </MenuContent>
                      </MenuRoot>
                    </HStack>
                  </Flex>

                  {/* Status + type — status as dot + label, type as muted pill */}
                  <HStack gap={2} flexWrap="wrap" align="center">
                    <HStack gap={1.5} align="center">
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg="var(--accent-success, #22C55E)"
                        flexShrink={0}
                      />
                      <Text
                        fontFamily="var(--font-mono)"
                        fontSize="10px"
                        letterSpacing="0.10em"
                        textTransform="uppercase"
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        Active
                      </Text>
                    </HStack>
                    <Badge colorPalette="blue" size="sm" variant="subtle">
                      {widget.displayType ?? "popup"}
                    </Badge>
                  </HStack>

                  {/* Linked server badges */}
                  {widget.serverMappings &&
                    widget.serverMappings.length > 0 && (
                      <Flex gap={1} flexWrap="wrap">
                        {widget.serverMappings.map((m) => (
                          <Badge
                            key={m.serverId}
                            colorPalette="purple"
                            size="sm"
                            variant="subtle"
                          >
                            {m.server?.name ?? "Server"}
                          </Badge>
                        ))}
                      </Flex>
                    )}
                </Flex>
              </Box>
            );
          })}

          {/* Add new widget card */}
          <Box
            minW="120px"
            p={3.5}
            borderRadius="10px"
            borderWidth="1px"
            borderColor="var(--border-default)"
            borderStyle="dashed"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            color="var(--text-secondary-color)"
            _hover={{
              borderColor: "var(--brand-primary, #3B82F6)",
              color: "var(--brand-primary, #3B82F6)",
            }}
            transition="border-color 0.15s, color 0.15s"
            onClick={() => setIsModalOpen(true)}
          >
            <Flex direction="column" align="center" gap={1}>
              <MdAdd size={20} />
              <Text
                fontFamily="var(--font-mono)"
                fontSize="10px"
                letterSpacing="0.10em"
                textTransform="uppercase"
                m={0}
              >
                New widget
              </Text>
            </Flex>
          </Box>
        </HStack>
      </Box>

      <CreateWidgetModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onWidgetCreated={handleWidgetCreated}
      />
    </>
  );
};

export default WidgetSelector;
